/**
 * PhotoCapture - cattura foto da camera (mobile) o file upload (desktop).
 * Le foto vengono compresse client-side e ritornate come data URL base64.
 *
 * Props:
 *   - photos: array di stringhe (data URL) - controllato
 *   - onChange(newPhotos): callback con il nuovo array
 *   - max: numero massimo di foto (default 5)
 *   - label: etichetta della sezione
 */
import { useRef } from 'react';
import { Camera, X, ImagePlus } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const compressImage = (file, maxSize = 1600, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const PhotoCapture = ({ photos = [], onChange, max = 5, label = 'Foto intervento' }) => {
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = max - photos.length;
    if (remaining <= 0) {
      toast.error(`Massimo ${max} foto`);
      return;
    }
    const toProcess = Array.from(files).slice(0, remaining);
    const compressed = [];
    for (const f of toProcess) {
      try {
        compressed.push(await compressImage(f));
      } catch (e) {
        toast.error(`Impossibile caricare ${f.name}`);
      }
    }
    onChange([...photos, ...compressed]);
  };

  const removePhoto = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          {label} <span className="text-muted-foreground/60">({photos.length}/{max})</span>
        </label>
        <div className="flex gap-2">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-camera-input"
          />
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraRef.current?.click()}
            disabled={photos.length >= max}
            className="rounded-md gap-2 text-xs"
            data-testid="photo-camera-btn"
          >
            <Camera className="h-4 w-4" /> Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => uploadRef.current?.click()}
            disabled={photos.length >= max}
            className="rounded-md gap-2 text-xs"
            data-testid="photo-upload-btn"
          >
            <ImagePlus className="h-4 w-4" /> Galleria
          </Button>
        </div>
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {photos.map((src, idx) => (
            <div key={`${src.length}-${src.slice(-24)}-${idx}`} className="relative group" data-testid={`photo-${idx}`}>
              <img src={src} alt={`foto-${idx + 1}`} className="w-full h-24 object-cover rounded-md border border-border" />
              <button
                type="button"
                onClick={() => removePhoto(idx)}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-remove-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {photos.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">Nessuna foto. Scatta una foto con la camera o carica dalla galleria.</p>
      )}
    </div>
  );
};

export default PhotoCapture;
